import type { FilterConfig, FilterOption } from '@openx/types/chipFilters.ts';
import { type KeyboardEvent, type SyntheticEvent, useCallback, useMemo } from 'react';
import { DealsMoreFiltersAutocompleteField } from './DealsMoreFiltersAutocompleteField.tsx';
import { getSelectedFilterOption, getVisibleFilterOptions } from './dealsMoreFiltersShared.ts';

export interface DealsMoreFiltersSelectFieldProps {
  filterConfig: FilterConfig;
  draftValue: string;
  onDraftChange: (value: string) => void;
  onApplyCurrentFilter: (value: string) => void;
  dataTest?: string;
}

const filterOptions = (opts: FilterOption[], value: string): FilterOption[] => {
  const valueLower = value.toLowerCase();
  return opts.filter(opt => (opt.displayName || opt.value).toString().toLowerCase().includes(valueLower));
};

export function DealsMoreFiltersSelectField({
  filterConfig,
  draftValue,
  onDraftChange,
  onApplyCurrentFilter,
  dataTest = 'deals-more-filters-section',
}: DealsMoreFiltersSelectFieldProps) {
  const { options = [], onInputChange, inputType, clearOnBlur } = filterConfig;

  const filteredEmptyOptions = useMemo(() => getVisibleFilterOptions(options), [options]);

  const currentOption = useMemo(
    () => getSelectedFilterOption(filterConfig, draftValue, { allowTextValue: inputType === 'text' }),
    [draftValue, filterConfig, inputType]
  );

  const handleOnInputChange = useCallback(
    (event: SyntheticEvent, value: string) => {
      if (event?.type !== 'change') {
        return;
      }

      if (inputType === 'text') {
        onDraftChange(value);
      }

      onInputChange?.(value);
    },
    [inputType, onDraftChange, onInputChange]
  );

  const handleChange = useCallback(
    (_: SyntheticEvent, option: FilterOption | string | null) => {
      if (!option) {
        onDraftChange('');
        return;
      }

      if (typeof option === 'string') {
        onDraftChange(option);
        return;
      }

      onDraftChange(String(option.value ?? ''));
    },
    [onDraftChange]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' && currentOption?.value) {
        onApplyCurrentFilter(String(currentOption.value));
      }
    },
    [currentOption, onApplyCurrentFilter]
  );

  const handleBlur = useCallback(() => {
    if (clearOnBlur && onInputChange) {
      onInputChange('');
    }
  }, [clearOnBlur, onInputChange]);

  const handleClearSelected = useCallback(() => {
    onDraftChange('');
  }, [onDraftChange]);

  return (
    <DealsMoreFiltersAutocompleteField
      filterConfig={filterConfig}
      currentOption={currentOption}
      inputValue={inputType === 'text' ? draftValue : undefined}
      options={filteredEmptyOptions}
      filterOptions={(availableOptions, params) => filterOptions(availableOptions, params.inputValue)}
      onChange={handleChange}
      onInputChange={handleOnInputChange}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      onClearSelected={handleClearSelected}
      clearDisabled={!draftValue}
      freeSolo={inputType === 'text'}
      dataTest={dataTest}
    />
  );
}
